import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Skull, Zap, ChevronRight, ShieldAlert, Timer, Flame } from 'lucide-react';
import { ESCAPE_SEQUENCE } from '@/config/missionContent';

const ESCAPE_DURATION = 10; // seconds
const REQUIRED_PRESSES = 60; // space presses needed to survive
const DISTANCE_TOTAL = 100; // virtual distance to cover

function Glitch({ active, intensity }) {
  if (!active) return null;
  return (
    <>
      <motion.div
        animate={{ opacity: [0, 0.8, 0, 0.5, 0] }}
        transition={{ duration: 0.15, repeat: Infinity, repeatDelay: Math.random() * 0.5 }}
        className="fixed inset-0 pointer-events-none z-[80]"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,0,0,0.03) 2px, transparent 4px)',
          mixBlendMode: 'overlay',
        }}
      />
      {/* Horizontal glitch bars that intensify over time */}
      {intensity > 0.5 && (
        <motion.div
          animate={{ opacity: [0, 0.15, 0], y: [0, -200, 100] }}
          transition={{ duration: 0.3, repeat: Infinity, repeatDelay: 0.8 }}
          className="fixed inset-0 pointer-events-none z-[81]"
          style={{
            background: `repeating-linear-gradient(0deg, transparent 0px, transparent ${40 - intensity * 20}px, rgba(255,0,0,0.08) ${40 - intensity * 20}px, transparent ${42 - intensity * 20}px)`,
          }}
        />
      )}
    </>
  );
}

function FallingDebris({ active, intensity }) {
  if (!active) return null;
  const count = Math.floor(15 + intensity * 30);
  return (
    <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden">
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          initial={{ y: -20, x: `${Math.random() * 100}%`, opacity: 0 }}
          animate={{
            y: ['0vh', '110vh'],
            opacity: [0, 0.8, 0],
            rotate: Math.random() * 720,
          }}
          transition={{
            duration: 1 + Math.random() * 1.5,
            delay: Math.random() * 3,
            repeat: Infinity,
            repeatDelay: Math.random() * (2 - intensity),
          }}
          className="absolute"
          style={{
            width: 2 + Math.random() * (6 + intensity * 6),
            height: 2 + Math.random() * (6 + intensity * 6),
            background: ['#ef4444', '#f97316', '#eab308', '#dc2626'][Math.floor(Math.random() * 4)],
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          }}
        />
      ))}
    </div>
  );
}

function WarningStripes() {
  return (
    <>
      <div
        className="fixed inset-x-0 top-0 h-2 z-[50]"
        style={{
          background: 'repeating-linear-gradient(135deg, #ef4444, #ef4444 10px, #1a1a1a 10px, #1a1a1a 20px)',
          animation: 'scroll-stripes 1s linear infinite',
        }}
      />
      <div
        className="fixed inset-x-0 bottom-0 h-2 z-[50]"
        style={{
          background: 'repeating-linear-gradient(135deg, #ef4444, #ef4444 10px, #1a1a1a 10px, #1a1a1a 20px)',
          animation: 'scroll-stripes 1s linear infinite',
        }}
      />
    </>
  );
}

function CorridorView({ progress, intensity }) {
  // Perspective corridor that collapses behind you
  return (
    <div className="absolute inset-0 pointer-events-none z-[3] overflow-hidden" style={{ perspective: '600px' }}>
      {/* Floor grid rushing toward camera */}
      <div
        className="absolute bottom-0 left-0 right-0"
        style={{
          height: '40%',
          transform: 'rotateX(60deg)',
          transformOrigin: 'bottom center',
        }}
      >
        <motion.div
          animate={{ backgroundPositionY: ['0px', '40px'] }}
          transition={{ duration: 0.3, repeat: Infinity, ease: 'linear' }}
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(239,68,68,${0.06 + intensity * 0.08}) 1px, transparent 1px),
              linear-gradient(0deg, rgba(239,68,68,${0.06 + intensity * 0.08}) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Ceiling grid */}
      <div
        className="absolute top-0 left-0 right-0"
        style={{
          height: '30%',
          transform: 'rotateX(-60deg)',
          transformOrigin: 'top center',
        }}
      >
        <motion.div
          animate={{ backgroundPositionY: ['0px', '40px'] }}
          transition={{ duration: 0.3, repeat: Infinity, ease: 'linear' }}
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(239,68,68,0.04) 1px, transparent 1px),
              linear-gradient(0deg, rgba(239,68,68,0.04) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Collapse wall behind — moves in as progress increases */}
      <motion.div
        className="absolute left-0 right-0 bottom-0"
        style={{
          height: '100%',
          background: `linear-gradient(0deg,
            rgba(239,68,68,${0.1 + intensity * 0.2}) 0%,
            rgba(220,38,38,${0.05 + intensity * 0.1}) 40%,
            transparent 80%
          )`,
        }}
        animate={{ opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 0.4 + (1 - intensity) * 0.6, repeat: Infinity }}
      />

      {/* Side walls with lights */}
      <div className="absolute left-0 top-0 bottom-0 w-px" style={{ background: `rgba(239,68,68,${0.1 + intensity * 0.15})` }} />
      <div className="absolute right-0 top-0 bottom-0 w-px" style={{ background: `rgba(239,68,68,${0.1 + intensity * 0.15})` }} />
    </div>
  );
}

function SpeedIndicator({ pps }) {
  // Presses per second gauge
  const clampedPps = Math.min(12, pps);
  const ratio = clampedPps / 12;
  const color = ratio > 0.6 ? '#4ade80' : ratio > 0.3 ? '#fbbf24' : '#ef4444';

  return (
    <div className="flex items-center gap-2">
      <Zap size={12} style={{ color }} />
      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
        <motion.div
          className="h-full rounded-full"
          style={{ width: `${ratio * 100}%`, background: color, transition: 'width 0.2s, background 0.2s' }}
        />
      </div>
      <span className="text-xs font-terminal tabular-nums" style={{ color: 'rgba(255,255,255,0.3)', minWidth: '28px' }}>
        {pps.toFixed(1)}/s
      </span>
    </div>
  );
}

export default function EscapeSequence({ onComplete }) {
  const [phase, setPhase] = useState('breach'); // breach -> running -> survived -> dead
  const [timeLeft, setTimeLeft] = useState(ESCAPE_DURATION);
  const [presses, setPresses] = useState(0);
  const [distance, setDistance] = useState(0);
  const [screenShake, setScreenShake] = useState(false);
  const [pps, setPps] = useState(0); // presses per second
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const distanceRef = useRef(0); // ref mirror to avoid stale closure in timer
  const pressTimesRef = useRef([]); // track press timestamps for PPS

  // Full reset on mount — fixes stale state when navigating back via dev controls
  useEffect(() => {
    setPhase('breach');
    setTimeLeft(ESCAPE_DURATION);
    setPresses(0);
    setDistance(0);
    setScreenShake(false);
    setPps(0);
    distanceRef.current = 0;
    pressTimesRef.current = [];
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    startTimeRef.current = null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep distance ref in sync
  useEffect(() => {
    distanceRef.current = distance;
  }, [distance]);

  // Breach intro -> running phase
  useEffect(() => {
    if (phase !== 'breach') return;
    const t = setTimeout(() => setPhase('running'), 5800);
    return () => clearTimeout(t);
  }, [phase]);

  // Timer countdown during running — no dependency on distance
  useEffect(() => {
    if (phase !== 'running') return;
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const remaining = Math.max(0, ESCAPE_DURATION - elapsed);
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(timerRef.current);
        if (distanceRef.current >= DISTANCE_TOTAL) {
          setPhase('survived');
        } else {
          setPhase('dead');
        }
      }
    }, 50);

    return () => clearInterval(timerRef.current);
  }, [phase]); // only depends on phase — NOT distance

  // PPS calculation
  useEffect(() => {
    if (phase !== 'running') return;
    const interval = setInterval(() => {
      const now = Date.now();
      // Keep only presses in last 1 second
      pressTimesRef.current = pressTimesRef.current.filter(t => now - t < 1000);
      setPps(pressTimesRef.current.length);
    }, 200);
    return () => clearInterval(interval);
  }, [phase]);

  // Increment logic — shared between keyboard and tap
  const doPress = useCallback(() => {
    if (phase !== 'running') return;

    pressTimesRef.current.push(Date.now());

    setPresses(p => p + 1);
    setDistance(d => {
      const newD = Math.min(DISTANCE_TOTAL, d + (DISTANCE_TOTAL / REQUIRED_PRESSES));
      if (newD >= DISTANCE_TOTAL) {
        clearInterval(timerRef.current);
        setTimeout(() => setPhase('survived'), 300);
      }
      return newD;
    });
    setScreenShake(true);
    setTimeout(() => setScreenShake(false), 60);
  }, [phase]);

  // Space key handler
  useEffect(() => {
    if (phase !== 'running') return;
    const handleKey = (e) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        doPress();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [phase, doPress]);

  // Tap handler for mobile
  const handleTap = useCallback(() => {
    doPress();
  }, [doPress]);

  const handleRetry = () => {
    setPresses(0);
    setDistance(0);
    setTimeLeft(ESCAPE_DURATION);
    pressTimesRef.current = [];
    setPps(0);
    setPhase('breach');
  };

  const progress = (distance / DISTANCE_TOTAL) * 100;
  const intensity = 1 - timeLeft / ESCAPE_DURATION; // 0 at start, 1 at end

  return (
    <motion.div
      animate={screenShake ? { x: [0, -3, 4, -2, 2, 0], y: [0, 2, -3, 1, 0] } : {}}
      transition={{ duration: 0.06 }}
      className="min-h-screen relative overflow-hidden"
      style={{ background: '#0a0000' }}
      data-testid="escape-sequence"
    >
      <Glitch active={phase === 'running' || phase === 'breach'} intensity={intensity} />
      <FallingDebris active={phase === 'running'} intensity={intensity} />
      {phase === 'running' && <WarningStripes />}
      {phase === 'running' && <CorridorView progress={progress} intensity={intensity} />}

      {/* Red pulsing vignette — intensifies over time */}
      {(phase === 'running' || phase === 'breach') && (
        <motion.div
          animate={{ opacity: [0.3 + intensity * 0.3, 0.5 + intensity * 0.3, 0.3 + intensity * 0.3] }}
          transition={{ duration: Math.max(0.3, 0.8 - intensity * 0.5), repeat: Infinity }}
          className="fixed inset-0 pointer-events-none z-[4]"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 20%, rgba(220,38,38,0.2) 100%)',
          }}
        />
      )}

      {/* ── BREACH PHASE ── */}
      <AnimatePresence>
        {phase === 'breach' && (
          <motion.div
            key="breach"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ exit: { duration: 0.3 } }}
            className="fixed inset-0 z-40 flex items-center justify-center"
            style={{ background: '#0a0000' }}
          >
            {/* Full-screen red flash on "breach" */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.6, 0, 0.3, 0] }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="absolute inset-0"
              style={{ background: '#ef4444' }}
            />

            {/* Pulsing static noise */}
            <motion.div
              animate={{ opacity: [0, 0.06, 0, 0.04, 0] }}
              transition={{ duration: 0.2, repeat: Infinity, repeatDelay: 0.3 }}
              className="absolute inset-0"
              style={{
                backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.5\'/%3E%3C/svg%3E")',
                backgroundSize: '200px',
              }}
            />

            {/* Expanding shockwave rings */}
            {[0, 0.15, 0.3].map((delay, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, opacity: 0.8 }}
                animate={{ scale: 4, opacity: 0 }}
                transition={{ duration: 1.5, delay: 0.2 + delay, ease: 'easeOut' }}
                className="absolute rounded-full"
                style={{
                  width: 120,
                  height: 120,
                  border: `2px solid rgba(239,68,68,${0.6 - i * 0.15})`,
                  boxShadow: `0 0 30px rgba(239,68,68,${0.2 - i * 0.05})`,
                }}
              />
            ))}

            {/* Radiating danger lines */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {Array.from({ length: 12 }).map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ scaleX: 0, opacity: 0 }}
                  animate={{ scaleX: 1, opacity: [0, 0.15, 0] }}
                  transition={{ duration: 0.8, delay: 0.5 + i * 0.08 }}
                  className="absolute left-0 right-0"
                  style={{
                    top: `${8 + i * 7.5}%`,
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(239,68,68,0.6), transparent)',
                    transformOrigin: 'center',
                  }}
                />
              ))}
            </div>

            <div className="text-center relative z-10">
              {/* Icon with explosive entrance */}
              <motion.div
                initial={{ scale: 5, opacity: 0, rotate: 45 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: 0.8 }}
                >
                  <ShieldAlert
                    size={90}
                    className="mx-auto mb-6"
                    style={{ color: '#ef4444', filter: 'drop-shadow(0 0 40px rgba(239,68,68,0.7)) drop-shadow(0 0 80px rgba(239,68,68,0.3))' }}
                  />
                </motion.div>
              </motion.div>

              {/* Title with glitch effect */}
              <div className="relative mb-4">
                {/* Glitch shadow copies */}
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.5, 0, 0.3, 0], x: [-3, 3, -2, 1, 0] }}
                  transition={{ duration: 0.15, repeat: Infinity, repeatDelay: 1.5, delay: 0.5 }}
                  className="text-4xl md:text-6xl font-bold tracking-[0.3em] absolute inset-0"
                  style={{ fontFamily: 'Orbitron', color: '#3b82f6', WebkitTextStroke: '0' }}
                  aria-hidden="true"
                >
                  {ESCAPE_SEQUENCE.breach.heading}
                </motion.h1>
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.5, 0, 0.3, 0], x: [3, -3, 2, -1, 0] }}
                  transition={{ duration: 0.15, repeat: Infinity, repeatDelay: 1.5, delay: 0.52 }}
                  className="text-4xl md:text-6xl font-bold tracking-[0.3em] absolute inset-0"
                  style={{ fontFamily: 'Orbitron', color: '#22c55e', WebkitTextStroke: '0' }}
                  aria-hidden="true"
                >
                  {ESCAPE_SEQUENCE.breach.heading}
                </motion.h1>
                {/* Main title */}
                <motion.h1
                  initial={{ opacity: 0, letterSpacing: '0.8em' }}
                  animate={{ opacity: 1, letterSpacing: '0.3em' }}
                  transition={{ duration: 0.4, delay: 0.5 }}
                  className="text-4xl md:text-6xl font-bold tracking-[0.3em] relative"
                  style={{
                    fontFamily: 'Orbitron',
                    color: '#ef4444',
                    textShadow: '0 0 30px rgba(239,68,68,0.6), 0 0 60px rgba(239,68,68,0.3), 0 0 100px rgba(239,68,68,0.15)',
                  }}
                >
                  {ESCAPE_SEQUENCE.breach.heading}
                </motion.h1>
              </div>

              {/* Divider line that draws in */}
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="w-48 h-px mx-auto mb-4"
                style={{ background: 'linear-gradient(90deg, transparent, #ef4444, transparent)', transformOrigin: 'center' }}
              />

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="text-sm tracking-[0.25em] mb-2"
                style={{ fontFamily: 'monospace', color: 'rgba(239,68,68,0.7)' }}
              >
                {ESCAPE_SEQUENCE.breach.status}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8 }}
                className="text-lg tracking-wider"
                style={{ fontFamily: 'Special Elite', color: 'rgba(251,146,60,0.9)' }}
              >
                {ESCAPE_SEQUENCE.breach.message}
              </motion.p>

              {/* Dramatic word sequence before "RUN" */}
              <div className="mt-8 relative h-20 flex items-center justify-center">
                {[
                  { text: 'NO TURNING BACK', delay: 2.2, size: 'text-xl md:text-2xl', color: 'rgba(239,68,68,0.8)' },
                  { text: 'GET OUT NOW', delay: 3.1, size: 'text-2xl md:text-3xl', color: 'rgba(251,146,60,0.9)' },
                  { text: 'Run,', delay: 4.0, size: 'text-3xl md:text-4xl', color: '#fbbf24' },
                ].map((item, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: [0, 1, 1, 0], y: [15, 0, 0, -10] }}
                    transition={{ duration: 0.8, delay: item.delay, times: [0, 0.15, 0.7, 1] }}
                    className={`absolute ${item.size} font-bold tracking-wider`}
                    style={{
                      fontFamily: 'Orbitron',
                      color: item.color,
                      textShadow: `0 0 25px ${item.color}80`,
                    }}
                  >
                    {item.text}
                  </motion.span>
                ))}
                {/* Red flash pulse on each word */}
                {[2.2, 3.1, 4.0].map((d, i) => (
                  <motion.div
                    key={`flash-${i}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 0.06 + i * 0.05, 0] }}
                    transition={{ duration: 0.25, delay: d }}
                    className="fixed inset-0 pointer-events-none"
                    style={{ background: '#ef4444' }}
                  />
                ))}
                {/* Final "RUN!" - stays visible */}
                <motion.p
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{ opacity: 1, scale: [0.3, 1.25, 1] }}
                  transition={{ duration: 0.5, delay: 4.6, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute text-4xl md:text-5xl font-bold tracking-widest"
                  style={{
                    fontFamily: 'Orbitron',
                    color: '#fbbf24',
                    textShadow: '0 0 30px rgba(251,191,36,0.7), 0 0 60px rgba(251,191,36,0.4), 0 0 100px rgba(251,191,36,0.2)',
                  }}
                >
                  RUN!
                </motion.p>
                {/* Big flash on final RUN */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.25, 0] }}
                  transition={{ duration: 0.4, delay: 4.6 }}
                  className="fixed inset-0 pointer-events-none"
                  style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.3), transparent 60%)' }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── RUNNING PHASE ── */}
      {phase === 'running' && (
        <div
          className="fixed inset-0 z-10 flex flex-col items-center justify-center cursor-pointer select-none"
          onClick={handleTap}
          data-testid="escape-tap-area"
        >
          {/* Timer */}
          <motion.div
            animate={timeLeft <= 3 ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 0.3, repeat: Infinity }}
            className="mb-6 text-center"
          >
            <div className="flex items-center gap-2 justify-center mb-2">
              <Timer size={16} style={{ color: timeLeft <= 3 ? '#ef4444' : '#f97316' }} />
              <span className="text-xs tracking-[0.2em]" style={{ fontFamily: 'monospace', color: 'rgba(239,68,68,0.5)' }}>
                {ESCAPE_SEQUENCE.running.selfDestructIn}
              </span>
            </div>
            <span
              className="text-6xl md:text-8xl font-bold tabular-nums"
              style={{
                fontFamily: 'Orbitron',
                color: timeLeft <= 3 ? '#ef4444' : '#fbbf24',
                textShadow: `0 0 30px ${timeLeft <= 3 ? 'rgba(239,68,68,0.5)' : 'rgba(251,191,36,0.3)'}`,
              }}
              data-testid="escape-timer"
            >
              {timeLeft.toFixed(1)}
            </span>
          </motion.div>

          {/* Corridor progress bar */}
          <div className="relative w-80 md:w-96 mb-6">
            {/* Track */}
            <div
              className="h-4 rounded-full overflow-hidden relative"
              style={{
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(239,68,68,0.25)',
                boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)',
              }}
            >
              {/* Filled bar */}
              <motion.div
                className="h-full rounded-full relative overflow-hidden"
                style={{
                  width: `${progress}%`,
                  background: progress > 80
                    ? 'linear-gradient(90deg, #22c55e, #4ade80)'
                    : progress > 50
                    ? 'linear-gradient(90deg, #eab308, #fbbf24)'
                    : 'linear-gradient(90deg, #ef4444, #f97316)',
                  boxShadow: `0 0 12px ${progress > 80 ? 'rgba(34,197,94,0.5)' : progress > 50 ? 'rgba(234,179,8,0.5)' : 'rgba(239,68,68,0.5)'}`,
                  transition: 'width 0.1s',
                }}
              >
                {/* Shimmer */}
                <motion.div
                  animate={{ x: ['-100%', '200%'] }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    width: '30%',
                  }}
                />
              </motion.div>

              {/* Collapse zone creeping from left */}
              <motion.div
                className="absolute top-0 bottom-0 left-0"
                style={{
                  width: `${intensity * 40}%`,
                  background: 'linear-gradient(90deg, rgba(239,68,68,0.4), transparent)',
                  transition: 'width 0.5s',
                }}
              />
            </div>

            {/* Runner icon */}
            <motion.div
              className="absolute -top-7"
              style={{ left: `${Math.min(93, progress)}%`, transition: 'left 0.1s' }}
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 0.12, repeat: Infinity }}
            >
              <div
                className="text-base font-bold"
                style={{
                  fontFamily: 'monospace',
                  color: progress > 80 ? '#4ade80' : '#fbbf24',
                  textShadow: `0 0 6px ${progress > 80 ? 'rgba(74,222,128,0.6)' : 'rgba(251,191,36,0.6)'}`,
                }}
              >
                ▶▶
              </div>
            </motion.div>

            {/* Exit marker */}
            <div className="absolute -top-7 right-0">
              <div className="text-base" style={{ color: '#4ade80', textShadow: '0 0 8px rgba(74,222,128,0.5)' }}>
                ◈
              </div>
            </div>

            {/* Labels */}
            <div className="flex justify-between mt-1.5">
              <span className="text-xs" style={{ fontFamily: 'monospace', color: 'rgba(239,68,68,0.5)' }}>
                <Flame size={9} className="inline mr-1" style={{ color: '#ef4444' }} />
                COLLAPSE
              </span>
              <span className="text-xs" style={{ fontFamily: 'monospace', color: 'rgba(34,197,94,0.5)' }}>
                EXIT
              </span>
            </div>
          </div>

          {/* SPAM SPACE instruction */}
          <motion.div
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 0.35, repeat: Infinity }}
            className="text-center mb-4"
          >
            <motion.div
              className="inline-block px-8 py-4 rounded-2xl mb-3 relative overflow-hidden"
              style={{
                background: 'rgba(251,191,36,0.06)',
                border: '2px solid rgba(251,191,36,0.2)',
              }}
              animate={{
                borderColor: ['rgba(251,191,36,0.2)', 'rgba(251,191,36,0.5)', 'rgba(251,191,36,0.2)'],
                boxShadow: ['0 0 15px rgba(251,191,36,0.03)', '0 0 35px rgba(251,191,36,0.12)', '0 0 15px rgba(251,191,36,0.03)'],
              }}
              transition={{ duration: 0.5, repeat: Infinity }}
            >
              {/* Key press flash */}
              <AnimatePresence>
                {screenShake && (
                  <motion.div
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="absolute inset-0 rounded-2xl"
                    style={{ background: 'rgba(251,191,36,0.15)' }}
                  />
                )}
              </AnimatePresence>
              <p
                className="text-xl md:text-2xl font-bold tracking-wider"
                style={{ fontFamily: 'Orbitron', color: '#fbbf24' }}
              >
                {ESCAPE_SEQUENCE.running.spamSpace}
              </p>
            </motion.div>
            <p className="text-xs tracking-widest" style={{ fontFamily: 'monospace', color: 'rgba(251,191,36,0.35)' }}>
              {ESCAPE_SEQUENCE.running.instruction}
            </p>
          </motion.div>

          {/* Stats row */}
          <div className="flex items-center gap-6">
            <span className="text-xs tabular-nums" style={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.25)' }}>
              PRESSES: {presses}
            </span>
            <SpeedIndicator pps={pps} />
            <span className="text-xs tabular-nums" style={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.25)' }}>
              {Math.round(progress)}%
            </span>
          </div>

          {/* Intensifying red flash in final seconds */}
          {timeLeft <= 3 && (
            <motion.div
              animate={{ opacity: [0, 0.08 + (3 - timeLeft) * 0.06, 0] }}
              transition={{ duration: Math.max(0.15, 0.3 - (3 - timeLeft) * 0.05), repeat: Infinity }}
              className="fixed inset-0 pointer-events-none z-[6]"
              style={{ background: '#ef4444' }}
            />
          )}
        </div>
      )}

      {/* ── SURVIVED ── */}
      <AnimatePresence>
        {phase === 'survived' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            className="fixed inset-0 z-50 overflow-hidden"
            style={{ background: '#010a01' }}
          >
            {/* Phase 1: Bright white/orange explosion flash that fades to black */}
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 2, ease: 'easeOut' }}
              className="absolute inset-0 z-20"
              style={{ background: 'radial-gradient(circle at 50% 80%, rgba(255,255,255,0.8), rgba(251,146,60,0.4) 25%, rgba(239,68,68,0.15) 50%, transparent 70%)' }}
            />

            {/* Phase 2: Slow fade from warm to cool — red embers to calm green */}
            <motion.div
              initial={{ opacity: 0.6 }}
              animate={{ opacity: 0 }}
              transition={{ duration: 3, delay: 0.5, ease: 'easeOut' }}
              className="absolute inset-0 z-10"
              style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(239,68,68,0.08), rgba(251,146,60,0.04) 40%, transparent 70%)' }}
            />

            {/* Phase 3: Green safety glow fades in */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 3, delay: 2 }}
              className="absolute inset-0 z-[5]"
              style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(34,197,94,0.06), transparent 60%)' }}
            />

            {/* Falling embers (orange→green transition) */}
            <div className="absolute inset-0 pointer-events-none z-[6]">
              {Array.from({ length: 20 }).map((_, i) => {
                const isEarly = i < 10;
                return (
                  <motion.div
                    key={i}
                    initial={{ y: '-5vh', x: `${5 + Math.random() * 90}%`, opacity: 0 }}
                    animate={{
                      y: '110vh',
                      opacity: [0, isEarly ? 0.7 : 0.4, 0],
                    }}
                    transition={{
                      duration: 3 + Math.random() * 4,
                      delay: isEarly ? Math.random() * 2 : 2 + Math.random() * 3,
                      ease: 'easeIn',
                    }}
                    className="absolute"
                    style={{
                      width: 1.5 + Math.random() * 2.5,
                      height: 1.5 + Math.random() * 2.5,
                      background: isEarly
                        ? ['#f97316', '#ef4444', '#fbbf24'][Math.floor(Math.random() * 3)]
                        : ['#4ade80', '#22c55e', '#86efac'][Math.floor(Math.random() * 3)],
                      borderRadius: '50%',
                      filter: `blur(${Math.random() > 0.5 ? 1 : 0}px)`,
                    }}
                  />
                );
              })}
            </div>

            {/* Content — centered */}
            <div className="absolute inset-0 z-30 flex items-center justify-center">
              <div className="text-center max-w-lg mx-auto px-6">

                {/* Cinematic black bars (letterbox) */}
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: '8vh' }}
                  transition={{ duration: 1.5, delay: 1, ease: 'easeOut' }}
                  className="fixed top-0 left-0 right-0 z-40"
                  style={{ background: '#000' }}
                />
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: '8vh' }}
                  transition={{ duration: 1.5, delay: 1, ease: 'easeOut' }}
                  className="fixed bottom-0 left-0 right-0 z-40"
                  style={{ background: '#000' }}
                />

                {/* STATUS: ESCAPED — small terminal text */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 2 }}
                  className="mb-6"
                >
                  <span
                    className="text-xs tracking-[0.5em] uppercase"
                    style={{ fontFamily: 'monospace', color: 'rgba(74,222,128,0.5)' }}
                  >
                    {ESCAPE_SEQUENCE.survived.status}
                  </span>
                </motion.div>

                {/* Main title — fades up with weight */}
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 2.5, ease: [0.25, 0.1, 0.25, 1] }}
                  className="text-4xl md:text-6xl font-bold tracking-[0.2em] mb-4"
                  style={{
                    fontFamily: 'Orbitron',
                    color: '#e2e8f0',
                    textShadow: '0 0 60px rgba(74,222,128,0.2)',
                  }}
                >
                  {ESCAPE_SEQUENCE.survived.heading}
                </motion.h2>

                {/* Accent line */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 1.2, delay: 3.2, ease: 'easeOut' }}
                  className="w-24 h-[2px] mx-auto mb-8"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #4ade80, transparent)',
                    transformOrigin: 'center',
                  }}
                />

                {/* Narrative text — staggered fade */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  transition={{ duration: 1, delay: 3.8 }}
                  className="text-sm tracking-wider mb-3 leading-relaxed"
                  style={{ fontFamily: 'Special Elite', color: '#94a3b8' }}
                >
                  {ESCAPE_SEQUENCE.survived.flavor1}
                </motion.p>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.7 }}
                  transition={{ duration: 1, delay: 4.6 }}
                  className="text-base md:text-lg tracking-wide mb-14 leading-relaxed"
                  style={{ fontFamily: 'Special Elite', color: '#cbd5e1' }}
                >
                  {ESCAPE_SEQUENCE.survived.flavor2}
                </motion.p>

                {/* Proceed button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 5.5 }}
                  whileHover={{ borderColor: 'rgba(74,222,128,0.6)', color: '#4ade80' }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onComplete?.()}
                  className="px-10 py-3 rounded text-xs tracking-[0.3em] uppercase transition-all duration-300"
                  style={{
                    fontFamily: 'Orbitron',
                    color: '#94a3b8',
                    border: '1px solid rgba(148,163,184,0.2)',
                    background: 'transparent',
                  }}
                  data-testid="escape-complete-btn"
                >
                  {ESCAPE_SEQUENCE.survived.proceedButton} <ChevronRight size={12} className="inline ml-1" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── DEAD / FAILED ── */}
      <AnimatePresence>
        {phase === 'dead' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: 'rgba(10,0,0,0.95)' }}
          >
            <div className="text-center p-8">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ duration: 0.5 }}
              >
                <Skull size={64} className="mx-auto mb-6" style={{ color: '#ef4444', filter: 'drop-shadow(0 0 30px rgba(239,68,68,0.5))' }} />
              </motion.div>

              <h2
                className="text-3xl font-bold tracking-[0.2em] mb-3"
                style={{ fontFamily: 'Orbitron', color: '#ef4444', textShadow: '0 0 20px rgba(239,68,68,0.3)' }}
              >
                {ESCAPE_SEQUENCE.dead.heading}
              </h2>

              <p className="text-sm mb-6" style={{ fontFamily: 'Special Elite', color: 'rgba(239,68,68,0.5)' }}>
                {ESCAPE_SEQUENCE.dead.message}
              </p>

              <p className="text-xs mb-6" style={{ fontFamily: 'monospace', color: 'rgba(255,255,255,0.2)' }}>
                Distance: {Math.round(progress)}% | Presses: {presses}
              </p>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleRetry}
                className="px-10 py-4 rounded-xl text-sm tracking-wider"
                style={{
                  fontFamily: 'Orbitron',
                  color: '#ef4444',
                  border: '2px solid rgba(239,68,68,0.3)',
                  background: 'rgba(239,68,68,0.05)',
                }}
                data-testid="escape-retry-btn"
              >
                {ESCAPE_SEQUENCE.dead.retryButton}
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
